import { useEffect, useRef } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  SquaresFour, Users, ClipboardText, CalendarCheck, SignOut, Baby, CurrencyDollar,
  ChatCircleDots, DeviceTabletCamera, GearSix, CalendarDots, Images, IdentificationBadge, ChartBar,
  Receipt as ReceiptIcon, FileText, Chalkboard, ShieldCheck, ListMagnifyingGlass, UsersThree, ArrowsLeftRight, Globe,
  ClockCounterClockwise, DeviceTablet, Briefcase, HouseLine, ClipboardText as RequestIcon, UploadSimple,
} from "@phosphor-icons/react";
import { ROLE_LABELS, can, isAdmin, isStaff, isSuper, canFinance, canPayroll } from "@/lib/roles";
import { toast } from "sonner";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: SquaresFour, end: true, testid: "nav-overview", show: () => true },
  { to: "/admin/students", label: "Students", icon: Users, testid: "nav-students", show: (u) => can(u, "students") },
  { to: "/admin/families", label: "Families", icon: UsersThree, testid: "nav-families", show: (u) => isAdmin(u) },
  { to: "/admin/portal", label: "Family Portal", icon: HouseLine, testid: "nav-portal", show: (u) => isStaff(u) && can(u, "parent_requests") },
  { to: "/admin/classrooms", label: "Classrooms", icon: Chalkboard, testid: "nav-classrooms", show: (u) => can(u, "classrooms") },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance", show: (u) => isStaff(u) },
  { to: "/admin/attendance-log", label: "Attendance Log", icon: ClockCounterClockwise, testid: "nav-attendance-log", show: (u) => isStaff(u) },
  { to: "/admin/classroom", label: "Classroom Tablet", icon: DeviceTablet, testid: "nav-classroom-tablet", show: (u) => isStaff(u) },
  { to: "/admin/admissions", label: "Admissions", icon: ClipboardText, testid: "nav-admissions", show: (u) => isStaff(u) },
  { to: "/admin/messages", label: "Messages", icon: ChatCircleDots, testid: "nav-messages", show: (u) => can(u, "messages") && isStaff(u) },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDots, testid: "nav-calendar", show: (u) => isStaff(u) },
  { to: "/admin/forms", label: "Forms", icon: FileText, testid: "nav-forms", show: (u) => isStaff(u) },
  { to: "/admin/reports", label: "Reports", icon: ChartBar, testid: "nav-reports", show: (u) => can(u, "reports") && u.role !== "parent" },
  { to: "/admin/website", label: "Website", icon: Globe, testid: "nav-website", show: (u) => can(u, "site_content") },
  { to: "/admin/staff", label: "Staff", icon: IdentificationBadge, testid: "nav-staff", show: (u) => isStaff(u) },
  { to: "/admin/hiring", label: "Hiring", icon: Briefcase, testid: "nav-hiring", show: (u) => can(u, "job_postings") || can(u, "applicants") },
  { to: "/admin/finance", label: "Finance", icon: CurrencyDollar, testid: "nav-finance", show: (u) => canFinance(u) || canPayroll(u) },
  { to: "/admin/audit", label: "Audit Log", icon: ListMagnifyingGlass, testid: "nav-audit", show: (u) => can(u, "audit_logs") },
  { to: "/admin/permissions", label: "Permissions", icon: ShieldCheck, testid: "nav-permissions", show: (u) => isAdmin(u) },
  { to: "/admin/import", label: "Roster Import", icon: UploadSimple, testid: "nav-import", show: (u) => isAdmin(u) },
  { to: "/admin/settings", label: "Settings", icon: GearSix, testid: "nav-settings", show: () => true },
];

const parentNav = [
  { to: "/portal", label: "Home", icon: HouseLine, end: true, testid: "nav-home" },
  { to: "/portal/requests", label: "Requests", icon: RequestIcon, testid: "nav-requests" },
  { to: "/portal/history", label: "Daily History", icon: Baby, testid: "nav-children" },
  { to: "/portal/album", label: "Photo Album", icon: Images, testid: "nav-album" },
  { to: "/portal/forms", label: "Forms", icon: FileText, testid: "nav-forms" },
  { to: "/portal/calendar", label: "Calendar", icon: CalendarDots, testid: "nav-calendar" },
  { to: "/portal/messages", label: "Messages", icon: ChatCircleDots, testid: "nav-messages" },
  { to: "/portal/billing", label: "Tuition", icon: CurrencyDollar, testid: "nav-tuition" },
  { to: "/portal/statements", label: "Tax Statement", icon: ReceiptIcon, testid: "nav-statements" },
  { to: "/portal/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

const ACTIVITY = ["mousemove", "keydown", "click", "touchstart", "scroll"];

/** Signs the user out after the center's configured period of inactivity. */
function useIdleLogout(minutes, onIdle) {
  const last = useRef(Date.now());
  useEffect(() => {
    if (!minutes) return undefined;
    const bump = () => { last.current = Date.now(); };
    ACTIVITY.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => { if (Date.now() - last.current > minutes * 60000) onIdle(); }, 15000);
    return () => { ACTIVITY.forEach((e) => window.removeEventListener(e, bump)); clearInterval(t); };
  }, [minutes, onIdle]);
}

export default function AppLayout({ variant }) {
  const { user, logout, switchMode } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const items = variant === "parent" ? parentNav : adminNav.filter((i) => user && i.show(user));

  const doLogout = async () => {
    await logout();
    nav("/");
  };
  useIdleLogout(user?.idle_minutes, async () => { await logout(); nav("/login?timeout=1"); });

  const toggleMode = async () => {
    try {
      const u = await switchMode(user.mode === "guardian" ? "staff" : "guardian");
      nav(u.role === "parent" ? "/portal" : "/admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not switch view"); }
  };

  return (
    <div className="min-h-screen bg-background lg:flex">
      <aside className="lg:w-64 lg:min-h-screen bg-[#0F172A] text-white flex lg:flex-col print:hidden">
        <div className="p-6 flex lg:flex-col gap-4 w-full items-center lg:items-stretch">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="sidebar-brand">
            <span className="h-10 w-10 rounded-2xl bg-[#3B82F6] grid place-items-center font-display font-bold">TPL</span>
            <span className="hidden lg:block font-display font-bold leading-tight">Teach Play<br />to Learn</span>
          </Link>
          <nav className="flex lg:flex-col gap-1 lg:mt-8 overflow-x-auto w-full" aria-label="Main">
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} data-testid={i.testid}
                       className={({ isActive }) =>
                         `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24] ${
                           isActive ? "bg-[#3B82F6] text-white" : "text-white/60 hover:text-white hover:bg-white/10"
                         }`}
                       style={{ transition: "background-color .2s ease, color .2s ease" }}>
                <i.icon size={20} weight="duotone" /> <span>{i.label}</span>
              </NavLink>
            ))}
            {variant !== "parent" && isStaff(user) && (
              <Link to="/kiosk" data-testid="nav-kiosk"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap text-[#FBBF24] hover:bg-white/10"
                    style={{ transition: "background-color .2s ease" }}>
                <DeviceTabletCamera size={20} weight="duotone" /> <span>Kiosk Mode</span>
              </Link>
            )}
            {(user?.can_switch_guardian || user?.mode === "guardian") && (
              <button onClick={toggleMode} data-testid="switch-mode-btn"
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap text-[#10B981] hover:bg-white/10"
                      style={{ transition: "background-color .2s ease" }}>
                <ArrowsLeftRight size={20} weight="duotone" /> <span>{user.mode === "guardian" ? "Back to staff view" : "My family portal"}</span>
              </button>
            )}
          </nav>
          <div className="lg:mt-auto lg:pt-8 ml-auto lg:ml-0 shrink-0">
            <p className="hidden lg:block text-sm font-bold" data-testid="current-user-name">{user?.name}</p>
            <p className="hidden lg:block text-xs uppercase tracking-[0.18em] text-white/50 mt-1" data-testid="current-user-role">
              {user?.mode === "guardian" ? "Family view" : ROLE_LABELS[user?.role] || user?.role}{isSuper(user) ? "" : ""}
            </p>
            <Button onClick={doLogout} variant="ghost"
                    className="mt-0 lg:mt-4 text-white/70 hover:text-white hover:bg-white/10 rounded-xl px-3"
                    data-testid="logout-btn">
              <SignOut size={18} className="lg:mr-2" /> <span className="hidden lg:inline">Log out</span>
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 p-6 sm:p-8 lg:p-10 min-w-0">
        {typeof location.state?.dashboardReturn === "string" && location.state.dashboardReturn.startsWith("/admin?") && <Link className="block mb-5 text-sm text-blue-600 underline" to={location.state.dashboardReturn}>Back to Dashboard results</Link>}
        <Outlet />
      </main>
    </div>
  );
}
